/**
 * Casework 3D (Phase 3; FREESTANDING round, 2026-08-19): no mesh of its own — a
 * meshless ElementSolid whose single `openingInstances` entry routes it through the
 * SAME per-type instancing pipeline as doors/windows/objects (protoKey → one
 * prototype per type + a transform per element — the Phase-3 50-instances
 * acceptance). Position is now the object anatomy: a single-node `path` anchor at
 * the box's BACK-CENTER + `rotationDeg`, no host — converted from the original
 * wall-hosted model (anchored at hostOffsetFt along the host wall's path, pushed to
 * the wall face). See categories/casework/schema.ts and
 * commands/elements.ts#migrateCaseworkToFreestanding for the one-time migration that
 * reproduces every existing cabinet's exact prior world position under this model.
 */
import {
  tinted, objectAnchorPoint, counterEdgeTiers, counterEdgeInsetAt, MITERED_APRON_IN, SPLASH_T_FT, SPLASH_FULL_DEFAULT_FT,
  type Pt2, type ResolvedElement,
} from '@aj/bim-kernel'
import type { ElementSolid, CounterPiece } from '../wall/generate3d'

/** CASEWORK STUDIO stage 4: the slab's thickness (feet) — the run style's, else 1-1/2" */
export function counterThicknessFt(el: ResolvedElement): number {
  const t = Number(el.typeParams.counterThicknessIn)
  return (Number.isFinite(t) && t > 0 ? Math.min(4, Math.max(0.5, t)) : 1.5) / 12
}

/** SURFACE HOSTING round (2026-08-24): the TRUE usable top surface of a casework
 *  unit — the countertop's own top when one exists (base style, `hasCounter`
 *  not false: `zBase + z0 + h + 0.125`, the same 1.5" slab caseworkSolid's own
 *  `counter` piece builds below), else the cabinet body's own top (upper/tall
 *  styles, or a base unit with its counter explicitly suppressed). A faucet or
 *  sink hosting onto casework needs THIS, not caseworkSolid's own `zTop` field
 *  (which is deliberately the cabinet BODY's top — the counter's underside —
 *  used for plan-cut classification, not "where does something sit"). One
 *  formula, shared with caseworkSolid below so the two can never drift apart. */
export function caseworkTopZ(el: ResolvedElement): number | null {
  if (!el.level) return null
  const zBase = el.level.elevationFt
  const style = String(el.typeParams.style || 'base')
  const h = (el.params.heightFt as number | null) ?? (el.typeParams.heightFt as number) ?? 3
  const z0 = style === 'upper' ? ((el.typeParams.mountHeightFt as number) || 4.5) : 0
  // an appliance slot (a range stands there) carries no counter
  const hasCounter = style === 'base' && el.params.hasCounter !== false && el.typeParams.frontConfig !== 'slot'
  return zBase + z0 + h + (hasCounter ? counterThicknessFt(el) : 0)
}

export function caseworkSolid(el: ResolvedElement): ElementSolid | null {
  if (!el.level) return null
  const anchor = objectAnchorPoint(el)
  if (!anchor) return null
  const zBase = el.level.elevationFt
  const rotZ = (((el.params.rotationDeg as number) || 0) * Math.PI) / 180
  const style = String(el.typeParams.style || 'base')
  // TYPE/INSTANCE GOVERNANCE round (2026-08-09): heightFt is a nullable INSTANCE
  // override shadowing the type's key in resolved params — el.params.heightFt alone
  // would read null on an un-overridden instance, never el.typeParams.heightFt.
  const h = (el.params.heightFt as number | null) ?? (el.typeParams.heightFt as number) ?? 3
  const z0 = style === 'upper' ? ((el.typeParams.mountHeightFt as number) || 4.5) : 0

  // plan footprint: the box rect rotated/placed like the prototype — local x = ±w/2,
  // local y = 0..depth (anchor at back-center, +y = front — the objectKinds.ts
  // placement frame, shared with the freestanding `object` category). widthFt is a
  // plain INSTANCE param (many widths per type) — read el.params, never el.typeParams.
  const w = (el.params.widthFt as number) || 2
  const d = (el.typeParams.depthFt as number) || 2
  const cx = Math.cos(rotZ), sx = Math.sin(rotZ)
  const at = (lx: number, ly: number): Pt2 =>
    [anchor[0] + lx * cx - ly * sx, anchor[1] + lx * sx + ly * cx]
  const fp = [at(-w / 2, 0), at(w / 2, 0), at(w / 2, d), at(-w / 2, d)]
  // CASEWORK STUDIO stage 5: a lazy susan / diagonal corner unit reaches out along the other wall
  const cg = cornerGeom(el, w, d)
  if (cg) fp.splice(0, fp.length, ...ccw(cg.footprint(0).map(([u, v]) => at(cg.x(u), v))))

  // countertop contribution (auto-join, 2026-07-28 decision): the slab footprint in
  // WORLD space — same 1" overhang the proto used to build (sides + front, never the
  // back). Overhangs make abutting/corner units' slabs physically overlap, which is
  // exactly what lets the join pass weld a run with plain union. `hasCounter`
  // (instance param) is the per-unit suppress escape hatch.
  const sm = el.typeParams.surfaceMaterial
  // an appliance slot (a range stands there) carries no counter
  const hasCounter = style === 'base' && el.params.hasCounter !== false && el.typeParams.frontConfig !== 'slot'
  // Stage 3: the countertop's own material wins; '' follows surfaceMaterial
  const ctrMat = typeof el.typeParams.counterMaterial === 'string' && el.typeParams.counterMaterial
    ? el.typeParams.counterMaterial as string
    // COLORS: a counter that follows the surface follows its color too
    : typeof sm === 'string' && sm ? tinted(sm, el.memberColor) : 'default'
  const pieces = hasCounter ? counterPiecesFor(el, { at, w, d, zc0: zBase + z0 + h, zLevel: zBase, ctrMat }) : []
  // the slab tier with the outermost edge carries the plan outline; it is the primary piece
  const counter = pieces[0]

  // CLOSET ROD round: the architectural closet symbol — the shelf outline and the rod
  // line OVERHEAD (they sit above the cut, so they draw dashed by the standard pass),
  // and the GARMENTS on their hangers below the cut (solid): a row of slanted strokes
  // across the rod at 3-1/2" spacing, each with a short hook tick at the rod
  const closetLines: { pts: Pt2[]; z0: number }[] = []
  if (style === 'closet') {
    const t = 0.75 / 12
    const sd = Math.min((el.typeParams.shelfDepthFt as number) || 14 / 12, d)
    const rodZ = Math.min((el.typeParams.rodHeightFt as number) || 5.5, h - t - 0.1)
    const shelfZ = zBase + h - t
    const s4 = [at(-w / 2, 0), at(w / 2, 0), at(w / 2, sd), at(-w / 2, sd)]
    closetLines.push({ pts: [...s4, s4[0]], z0: shelfZ })
    const yc = d / 2
    closetLines.push({ pts: [at(-w / 2, yc), at(w / 2, yc)], z0: zBase + rodZ })
    const half = Math.max(0.25, Math.min(10 / 12, yc - 1 / 12))   // a garment's half-width
    const lean = 1.25 / 12, step = 3.5 / 12, edge = 2 / 12
    const gZ = zBase + Math.min(3, rodZ - 1)   // hanging garments: below the plan cut
    const span = w - 2 * edge
    const n = Math.max(1, Math.floor(span / step) + 1)
    const x0 = -(n - 1) * step / 2
    for (let i = 0; i < n; i++) {
      const x = x0 + i * step
      closetLines.push({ pts: [at(x - lean, yc - half), at(x + lean, yc + half)], z0: gZ })
    }
  }

  return {
    elementId: el.id,
    category: 'casework',
    outlines: [],
    zBase: zBase + z0,
    zTop: zBase + z0 + h,
    materialId: 'default',
    groupKey: `casework|${el.levelId}|${el.id}`,
    pieces: [],
    placements: [],
    openingInstances: [{ opening: el, pos: [anchor[0], anchor[1], zBase], rotZ }],
    meshOnly: true,
    // footprint at the cabinet's BASE elevation: base/tall read solid below the cut
    // plane, upper cabinets read dashed overhead (Matt's r2 audit)
    // CLOSET ROD round: a closet is no box — its symbol is the whole plan story
    // BAYS AND LAYERS: open wall draws nothing; floating shelves read at their own depth
    projLines: style === 'closet' ? closetLines
      : style === 'upper' && el.typeParams.upperCell === 'open' ? []
        : style === 'upper' && el.typeParams.upperCell === 'shelves'
          ? (() => { const sd = Math.min(d, 10 / 12); const q = [at(-w / 2, 0), at(w / 2, 0), at(w / 2, sd), at(-w / 2, sd)]; return [{ pts: [...q, q[0]], z0: zBase + z0 }] })()
          : [{ pts: [...fp, fp[0]], z0: zBase + z0 }],
    ...(counter ? { counter } : {}),
    ...(pieces.length > 1 ? { counterPieces: pieces.slice(1) } : {}),
  }
}

/** counter-clockwise (the counter join's union reads a clockwise ring as a hole) */
function ccw(pts: Pt2[]): Pt2[] {
  let a = 0
  for (let i = 0; i < pts.length; i++) { const p = pts[i], q = pts[(i + 1) % pts.length]; a += p[0] * q[1] - q[0] * p[1] }
  return a < 0 ? pts.slice().reverse() : pts
}

/** CASEWORK STUDIO stage 5: a corner unit's plan geometry in corner-local (u out along the run
 *  from the corner wall, v out from the back wall) — null for anything but a lazy susan or
 *  diagonal corner (a blind / dead corner keeps its box's own rectangle) */
export function cornerGeom(el: ResolvedElement, w: number, d: number) {
  const tp = el.typeParams
  const kind = tp.__corner
  const cDB = Number(tp.__cornerDB) || 0, cLeg = Number(tp.__cornerLeg) || 0
  // UPPER CORNERS (2026-10-15): an upper corner unit's plan outline follows its L / angled case too
  if ((kind !== 'lazySusan' && kind !== 'diagonal') || !(cDB > 0) || !(cLeg > d) || (tp.style !== 'base' && tp.style !== 'upper')) return null

  const cs = tp.__cornerSide === 'L' ? -1 : 1
  const x = (u: number) => cs * w / 2 - cs * u
  // outline grown by `o` on the faces into the room (the counter's front overhang)
  const footprint = (o: number): Pt2[] => {
    if (kind === 'lazySusan') return [[0, 0], [w, 0], [w, d + o], [cDB + o, d + o], [cDB + o, cLeg], [0, cLeg]]
    const len = Math.hypot(cDB - w, cLeg - d) || 1
    let n: Pt2 = [(cLeg - d) / len, -(cDB - w) / len]
    if (n[0] + n[1] < 0) n = [-n[0], -n[1]]
    const a: Pt2 = [w + n[0] * o, d + n[1] * o], b: Pt2 = [cDB + n[0] * o, cLeg + n[1] * o]
    // the moved face met with u = w and v = cLeg
    const atU = a[1] + (b[1] - a[1]) * ((w - a[0]) / ((b[0] - a[0]) || 1e-9))
    const atV = a[0] + (b[0] - a[0]) * ((cLeg - a[1]) / ((b[1] - a[1]) || 1e-9))
    return [[0, 0], [w, 0], [w, atU], [atV, cLeg], [0, cLeg]]
  }
  return { kind, cDB, cLeg, x, footprint }
}

/**
 * CASEWORK STUDIO stage 4 (2026-10-09): this box's share of the countertop, as footprints the
 * counter join welds across the run (one weld per piece kind and z band):
 *   slab tiers — the edge profile as stacked bands, each set back on the EXPOSED edges (the
 *     front, and an end where no neighbouring counter continues it); an interior joint reaches
 *     1" into the neighbour, exactly as the plain slab always did, so a run still welds
 *   apron     — a mitered edge's drop below the slab, on the exposed edges
 *   waterfall — the slab turned down an end to the floor
 *   splash    — the backsplash along the back, to 4", 6", or up to the uppers
 * The first piece returned is the slab tier with the outermost edge (the plan outline).
 */
function counterPiecesFor(
  el: ResolvedElement,
  g: { at: (lx: number, ly: number) => Pt2; w: number; d: number; zc0: number; zLevel: number; ctrMat: string },
): CounterPiece[] {
  const tp = el.typeParams
  const { at, w, d, zc0, ctrMat } = g
  const T = counterThicknessFt(el)
  const num = (v: unknown, dflt: number) => { const n = Number(v); return Number.isFinite(n) && n >= 0 ? n : dflt }
  const front = num(tp.counterFrontOverIn, 1) / 12
  const endOver = num(tp.counterEndOverIn, 1) / 12
  const ctrL = tp.__ctrL === true, ctrR = tp.__ctrR === true
  // COUNTER ENDS (2026-10-23): against a wall the top stops flush with the cabinet and has no edge
  // profile there; beside an appliance slot it stops flush but keeps its edge (doc.ts)
  const wallL = tp.__ctrWallL === true, wallR = tp.__ctrWallR === true
  const gapL = tp.__ctrGapL === true, gapR = tp.__ctrGapR === true
  const wf = String(tp.counterWaterfall || 'none')
  // seen from the room the run's start (local -x) is on the RIGHT
  const wfL = !ctrL && !wallL && (wf === 'right' || wf === 'both')
  const wfR = !ctrR && !wallR && (wf === 'left' || wf === 'both')
  const xL = ctrL ? -(w / 2 + 1 / 12) : wfL ? -(w / 2 + T) : wallL || gapL ? -w / 2 : -(w / 2 + endOver)
  const xR = ctrR ? w / 2 + 1 / 12 : wfR ? w / 2 + T : wallR || gapR ? w / 2 : w / 2 + endOver
  const profL = !ctrL && !wfL && !wallL, profR = !ctrR && !wfR && !wallR
  const yF = d + front
  const base = { materialId: ctrMat, depthFt: d, levelId: el.levelId }
  const rect = (x0: number, x1: number, y0: number, y1: number): Pt2[] => [at(x0, y0), at(x1, y0), at(x1, y1), at(x0, y1)]
  const edge = String(tp.counterEdge || 'eased')
  const tiers = counterEdgeTiers(edge, T)
  const minInset = Math.min(...tiers.map((t) => t.inset))
  const outlineAt = tiers.findIndex((t) => t.inset === minInset)
  const slabs: CounterPiece[] = tiers.map((t, i) => ({
    ...base, kind: 'slab', outline: i === outlineAt,
    poly: rect(xL + (profL ? t.inset : 0), xR - (profR ? t.inset : 0), 0, yF - t.inset),
    z0: zc0 + t.z0, z1: zc0 + t.z1,
  }))
  // STAGE 5: a lazy susan / diagonal corner unit's top follows its whole L / five-sided case
  const cg = cornerGeom(el, w, d)
  if (cg) {
    for (const [i, t] of tiers.entries()) {
      slabs[i].poly = ccw(cg.footprint(front - t.inset).map(([u, v]) => at(cg.x(u), v)))
    }
  }
  const out: CounterPiece[] = [slabs[outlineAt], ...slabs.filter((_, i) => i !== outlineAt)]
  // TRUE EDGES: the full outline and the profile's deepest set-back, welded across the run like
  // the tiers — the join sweeps the real profile between them for the 3D counter
  if (edge !== 'mitered') {
    let maxIn = 0
    for (let k = 0; k <= 32; k++) maxIn = Math.max(maxIn, counterEdgeInsetAt(edge, T, (T * k) / 32))
    const full = cg ? ccw(cg.footprint(front).map(([u, v]) => at(cg.x(u), v))) : rect(xL, xR, 0, yF)
    out.push({ ...base, kind: 'edgeFull', outline: false, poly: full, z0: zc0, z1: zc0 + T, edge, thickness: T })
    if (maxIn > 1e-5) {
      const inn = cg ? ccw(cg.footprint(front - maxIn).map(([u, v]) => at(cg.x(u), v)))
        : rect(xL + (profL ? maxIn : 0), xR - (profR ? maxIn : 0), 0, yF - maxIn)
      out.push({ ...base, kind: 'edgeIn', outline: false, poly: inn, z0: zc0, z1: zc0 + T, edge, thickness: T })
    }
  }
  // the mitered apron: the visible edge carried down below the slab
  const drop = edge === 'mitered' ? MITERED_APRON_IN / 12 - T : 0
  if (drop > 1e-4) {
    const a = 0.75 / 12
    const z = { z0: zc0 - drop, z1: zc0 }
    out.push({ ...base, kind: 'apron', outline: false, poly: rect(xL, xR, yF - a, yF), ...z })
    if (profL) out.push({ ...base, kind: 'apron', outline: false, poly: rect(xL, xL + a, 0, yF), ...z })
    if (profR) out.push({ ...base, kind: 'apron', outline: false, poly: rect(xR - a, xR, 0, yF), ...z })
  }
  // waterfall ends: the slab turned down the open end to the floor
  for (const [on, x0, x1] of [[wfL, -(w / 2 + T), -w / 2], [wfR, w / 2, w / 2 + T]] as const) {
    if (on) out.push({ ...base, kind: 'waterfall', outline: false, poly: rect(x0, x1, 0, yF), z0: g.zLevel, z1: zc0 })
  }
  // the backsplash
  const sp = String(tp.counterSplash || 'none')
  if (sp !== 'none') {
    const zTop = zc0 + T
    const top = sp === '4' ? zTop + 4 / 12 : sp === '6' ? zTop + 6 / 12
      : typeof tp.__splashTopFt === 'number' ? g.zLevel + tp.__splashTopFt : zTop + SPLASH_FULL_DEFAULT_FT
    if (top - zTop > 1 / 48) {
      const sm2 = typeof tp.splashMaterial === 'string' && tp.splashMaterial ? tp.splashMaterial as string : ctrMat
      // CASEWORK ROWS stage 3: the splash stops at the sides of a box standing on the counter
      const cuts = String(tp.__splashCuts || '').split(';').map((c) => c.split(',').map(Number))
        .filter((c) => c.length === 2 && c.every(Number.isFinite))
        // a cut reaching the box's edge runs on to the splash's end (the lap into the next box)
        .map(([a, c]) => [a <= -w / 2 + 1e-3 ? xL : a, c >= w / 2 - 1e-3 ? xR : c])
        .sort((a, c) => a[0] - c[0])
      let sx = xL
      const spans: [number, number][] = []
      for (const [a, c] of cuts) { if (a > sx + 1e-6) spans.push([sx, Math.min(a, xR)]); sx = Math.max(sx, c) }
      if (xR > sx + 1e-6) spans.push([sx, xR])
      for (const [a, c] of spans) {
        if (c - a < 1 / 96) continue
        out.push({
          materialId: sm2, depthFt: SPLASH_T_FT, levelId: el.levelId, kind: 'splash', outline: true,
          poly: rect(a, c, 0, SPLASH_T_FT), z0: zTop, z1: top,
        })
      }
      // STAGE 5: at a corner the backsplash turns and runs along the other wall, over the
      // corner square (or the corner unit's leg) to where the other run takes over
      const reach = Number(tp.__cornerReach) || 0
      if ((tp.__cornerWall === 'L' || tp.__cornerWall === 'R') && reach > SPLASH_T_FT) {
        const xw = tp.__cornerWall === 'L' ? -w / 2 : w / 2
        const x0 = Math.min(xw, xw - Math.sign(xw) * SPLASH_T_FT), x1 = Math.max(xw, xw - Math.sign(xw) * SPLASH_T_FT)
        out.push({
          materialId: sm2, depthFt: SPLASH_T_FT, levelId: el.levelId, kind: 'splash', outline: true,
          poly: rect(x0, x1, 0, reach + 1 / 12), z0: zTop, z1: top,
        })
      }
    }
  }
  return out
}
